/* eslint-disable max-lines */
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import MUIDialog from '@material-ui/core/Dialog';
import Slide from '@material-ui/core/Slide';
import Image from 'next/image';
import { forwardRef } from 'react';
import { Fonts, mediaQuery, Spacings } from '@styles/index';
import { useIsMobile } from '@stores/AppCommonData';
import VideoPreview from './VideoPreview';
import { parseMarkedText, renderMultilineMarkedText } from './parseMarkedText';
import {
  DEFAULT_PRIME_PITCH_BENEFIT_COLOUR,
  PrimeTrialPromoModalProps
} from './types';

const PRIME_PITCH_COLORS = {
  surface: '#100B20',
  heroPurple: '#312357',
  heroPurpleRgb: '49, 35, 87',
  cardBorder: '#363149',
  accent: '#9875FF',
  accentBorder: '#A687FF',
  white: '#FFFFFF',
  muted: 'rgba(255, 255, 255, 0.8)',
  mutedSoft: 'rgba(255, 255, 255, 0.6)',
  divider: 'rgba(255, 255, 255, 0.2)',
  offerCardBorder: 'rgba(169, 141, 252, 0.2)',
  ctaGlow: 'rgba(145, 108, 255, 0.8)',
  shimmer: 'rgba(255, 255, 255, 0.87)'
} as const;

const PRIME_PITCH_GRADIENTS = {
  heroFade: `linear-gradient(180deg, ${PRIME_PITCH_COLORS.heroPurple} 0%, rgba(${PRIME_PITCH_COLORS.heroPurpleRgb}, 0) 100%)`,
  offerBackdrop:
    'linear-gradient(-21deg, rgba(225, 182, 130, 0.2) 7.44%, rgba(104, 41, 193, 0.2) 81.05%)',
  benefitsList:
    'linear-gradient(178deg, rgba(43, 36, 65, 0.72) 44%, rgba(110, 92, 167, 0.72) 155%)'
} as const;

const PRIME_PITCH_RADIUS = {
  sheet: `${Spacings.SPACING_4B} ${Spacings.SPACING_4B} 0 0`,
  modal: Spacings.SPACING_4B,
  offerCard: Spacings.SPACING_3B,
  benefitsList: Spacings.SPACING_2B,
  cta: Spacings.SPACING_2B,
  ctaShimmer: Spacings.SPACING_6
} as const;

const PRIME_PITCH_TYPOGRAPHY = {
  renewTitle: {
    fontSize: Spacings.SPACING_6B,
    lineHeight: Spacings.SPACING_7B
  },
  trialPrimary: {
    fontSize: Spacings.SPACING_6B,
    lineHeight: Spacings.SPACING_6B,
    letterSpacing: '0.2px'
  },
  trialContin: {
    fontSize: Spacings.SPACING_10,
    lineHeight: Spacings.SPACING_3B
  },
  tagline: {
    fontSize: Spacings.SPACING_18,
    lineHeight: Spacings.SPACING_22
  },
  benefitsHeader: {
    fontSize: Spacings.SPACING_14,
    lineHeight: Spacings.SPACING_20,
    opacity: 0.9
  },
  benefitCount: {
    fontSize: Spacings.SPACING_4B,
    lineHeight: Spacings.SPACING_20
  },
  benefitLabel: {
    fontSize: Spacings.SPACING_14,
    lineHeight: Spacings.SPACING_4B,
    letterSpacing: '0.14px'
  },
  cta: {
    fontSize: Spacings.SPACING_4B
  }
} as const;

const PRIME_PITCH_ASSETS = {
  offerCardWaves: '/static/images/prime-pitch/offer-card-waves.svg',
  benefitsDividerLeft: '/static/images/prime-pitch/benefits-divider-left.svg',
  benefitsDividerRight: '/static/images/prime-pitch/benefits-divider-right.svg',
  benefitRowDivider: '/static/images/prime-pitch/benefit-row-divider.svg',
  primeBox: '/static/images/prime-pitch/prime-box.svg'
} as const;

const PRIME_PITCH_LAYOUT = {
  webModalWidth: 500,
  dialogViewportInset: Spacings.SPACING_8B,
  heroFadeHeight: 48,
  heroFadeOverlap: Spacings.SPACING_5,
  heroLogoOffset: Spacings.SPACING_4B,
  primeBadgeBottom: Spacings.SPACING_6B,
  primeBadgeHeight: 31,
  offerBackdropHeight: 91,
  offerBackdropTop: 20,
  offerBackdropPaddingTop: 0,
  offerBackdropPaddingBottom: 6,
  renewOfferBackdropPaddingTop: 16,
  renewOfferBackdropPaddingBottom: 12,
  renewOfferBackdropPaddingX: 20,
  offerSectionPullUp: 10,
  priceImageWidth: 114,
  priceImageHeight: 115,
  renewPriceImageWidth: 132,
  renewPriceImageHeight: 64,
  priceBulgeOffset: 50,
  renewTitleCopyWidth: 120,
  trialDividerHeight: 48,
  trialDividerTrialOffset: Spacings.SPACING_10,
  trialCopyWidth: 146,
  trialCopyTrialOffset: Spacings.SPACING_10,
  trialContinRowGap: Spacings.SPACING_1B,
  taglineTextMaxWidth: 186,
  taglineImageWidth: 155,
  taglineImageHeight: 167,
  taglineImagePullUp: 0,
  taglineImagePullDown: 0,
  benefitsHeaderDecorWidth: 48,
  benefitsHeaderDecorHeight: 11,
  benefitsHeaderGap: Spacings.SPACING_1B,
  benefitsListRowGap: Spacings.SPACING_2B,
  benefitCountWidth: 75,
  benefitIconSize: 30,
  ctaHeight: Spacings.SPACING_12B,
  ctaBorderWidth: '2px',
  ctaShimmerDurationMs: 1200,
  ctaShimmerStartOffset: '-12.2%',
  ctaShimmerEndOffset: '109.5%',
  ctaShimmerTrackTop: '-40px',
  ctaShimmerTrackHeight: 130
} as const;

const PRIME_PITCH_CTA_SHIMMER_STREAKS = [
  {
    left: '0%',
    top: Spacings.SPACING_2B,
    width: 15,
    height: 115,
    rotate: 43.69,
    blur: 6.2
  },
  {
    left: '87.5%',
    top: Spacings.SPACING_7B,
    width: 15,
    height: 115,
    rotate: -136.31,
    blur: 6.2
  },
  {
    left: '8%',
    top: '0',
    width: 24,
    height: 151,
    rotate: 43.69,
    blur: 7.6
  },
  {
    left: '71.5%',
    top: '0',
    width: 30,
    height: 130,
    rotate: -136.31,
    blur: 7.6
  }
] as const;

const CONTENT_PADDING = Spacings.SPACING_4B;

const Transition = forwardRef(function Transition(
  props: Record<string, unknown>,
  ref
) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const StyledDialog = styled(MUIDialog)<{ $isMobile: boolean }>`
  &.MuiDialog-root .MuiDialog-container {
    align-items: ${({ $isMobile }) => ($isMobile ? 'flex-end' : 'center')};
    padding: ${({ $isMobile }) => ($isMobile ? '0' : Spacings.SPACING_4B)};
  }

  &.MuiDialog-root .MuiDialog-container .MuiPaper-root {
    margin: 0;
    width: ${({ $isMobile }) =>
      $isMobile ? '100%' : `${PRIME_PITCH_LAYOUT.webModalWidth}px`};
    max-width: ${({ $isMobile }) =>
      $isMobile ? '100%' : `${PRIME_PITCH_LAYOUT.webModalWidth}px`};
    max-height: calc(100vh - ${PRIME_PITCH_LAYOUT.dialogViewportInset});
    border-radius: ${({ $isMobile }) =>
      $isMobile ? PRIME_PITCH_RADIUS.sheet : PRIME_PITCH_RADIUS.modal};
    background: transparent;
    box-shadow: none;
    overflow: hidden;

    ${mediaQuery.uptoMobile} {
      width: 100%;
      max-width: 100%;
      border-radius: ${PRIME_PITCH_RADIUS.sheet};
    }
  }
`;

const ModalSurface = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-height: calc(100vh - ${PRIME_PITCH_LAYOUT.dialogViewportInset});
  height: auto;
  overflow: hidden;
  background: ${PRIME_PITCH_COLORS.surface};
  border-radius: inherit;
`;

const ScrollArea = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  overscroll-behavior: contain;
`;

const HeroFullBleed = styled.div`
  position: relative;
  width: 100%;
  max-width: 100%;
  flex-shrink: 0;
  margin: 0;
  padding: 0;
  overflow: hidden;
  border-top-left-radius: inherit;
  border-top-right-radius: inherit;
`;

const Hero = styled.div`
  position: relative;
  width: 100%;
  margin: 0;
  padding: 0;
`;

const Logo = styled(Image)`
  position: absolute;
  top: ${PRIME_PITCH_LAYOUT.heroLogoOffset};
  left: ${PRIME_PITCH_LAYOUT.heroLogoOffset};
  z-index: 2;
`;

const HeroFade = styled.div`
  height: ${PRIME_PITCH_LAYOUT.heroFadeHeight}px;
  margin-top: -${PRIME_PITCH_LAYOUT.heroFadeOverlap};
  background: ${PRIME_PITCH_GRADIENTS.heroFade};
`;

const PrimeBadgeFloat = styled.div`
  position: absolute;
  left: 50%;
  bottom: ${PRIME_PITCH_LAYOUT.primeBadgeBottom};
  transform: translateX(-50%);
  z-index: 5;
`;

const PrimeBadgeImage = styled(Image)`
  display: block;
  width: auto;
  height: ${PRIME_PITCH_LAYOUT.primeBadgeHeight}px;
`;

const PaddedContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};
  width: 100%;
  box-sizing: border-box;
  padding: 0 ${CONTENT_PADDING} ${Spacings.SPACING_3B};
  overflow: visible;
`;

const OfferSection = styled.div<{
  $hasHeroVideo?: boolean;
  $isRenew?: boolean;
}>`
  position: relative;
  z-index: 1;
  margin-top: ${({ $hasHeroVideo, $isRenew }) =>
    $hasHeroVideo && !$isRenew
      ? `-${PRIME_PITCH_LAYOUT.offerSectionPullUp}px`
      : '0'};
  min-height: 0;
  padding-top: ${({ $isRenew }) =>
    $isRenew ? 0 : `${PRIME_PITCH_LAYOUT.offerBackdropTop}px`};
  overflow: visible;
`;

const OfferBackdrop = styled.div<{ $isRenew?: boolean }>`
  position: absolute;
  left: 0;
  right: 0;
  top: ${({ $isRenew }) =>
    $isRenew ? 0 : `${PRIME_PITCH_LAYOUT.offerBackdropTop}px`};
  ${({ $isRenew }) =>
    $isRenew
      ? 'bottom: 0;'
      : `height: ${PRIME_PITCH_LAYOUT.offerBackdropHeight}px;`}
  border: 1px solid ${PRIME_PITCH_COLORS.offerCardBorder};
  border-radius: ${PRIME_PITCH_RADIUS.offerCard};
  background: ${PRIME_PITCH_GRADIENTS.offerBackdrop};
  overflow: hidden;
  pointer-events: none;
`;

const OfferWaves = styled.img`
  position: absolute;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100%;
  height: auto;
  min-height: 100%;
  object-fit: cover;
  object-position: center bottom;
  pointer-events: none;
`;

const TrialBlock = styled.div<{ $isRenew?: boolean }>`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  width: 100%;
  padding: ${({ $isRenew }) =>
    $isRenew
      ? `${PRIME_PITCH_LAYOUT.renewOfferBackdropPaddingTop}px ${PRIME_PITCH_LAYOUT.renewOfferBackdropPaddingX}px ${PRIME_PITCH_LAYOUT.renewOfferBackdropPaddingBottom}px`
      : `${PRIME_PITCH_LAYOUT.offerBackdropPaddingTop}px 0 ${PRIME_PITCH_LAYOUT.offerBackdropPaddingBottom}px`};
  box-sizing: border-box;
`;

const TrialTopRow = styled.div<{ $isRenew?: boolean }>`
  display: flex;
  align-items: ${({ $isRenew }) => ($isRenew ? 'center' : 'flex-end')};
  justify-content: center;
  gap: ${Spacings.SPACING_2B};
  width: 100%;
`;

const PriceImageWrap = styled.div<{ $isRenew?: boolean }>`
  position: relative;
  z-index: 4;
  flex-shrink: 0;
  width: ${({ $isRenew }) =>
    $isRenew
      ? PRIME_PITCH_LAYOUT.renewPriceImageWidth
      : PRIME_PITCH_LAYOUT.priceImageWidth}px;
  height: ${({ $isRenew }) =>
    $isRenew
      ? PRIME_PITCH_LAYOUT.renewPriceImageHeight
      : PRIME_PITCH_LAYOUT.priceImageHeight}px;
  margin-top: ${({ $isRenew }) =>
    $isRenew ? 0 : `-${PRIME_PITCH_LAYOUT.priceBulgeOffset}px`};
  line-height: 0;
  overflow: ${({ $isRenew }) => ($isRenew ? 'hidden' : 'visible')};

  img {
    width: ${({ $isRenew }) =>
      $isRenew
        ? PRIME_PITCH_LAYOUT.renewPriceImageWidth
        : PRIME_PITCH_LAYOUT.priceImageWidth}px;
    height: ${({ $isRenew }) =>
      $isRenew
        ? PRIME_PITCH_LAYOUT.renewPriceImageHeight
        : PRIME_PITCH_LAYOUT.priceImageHeight}px;
    max-width: ${({ $isRenew }) =>
      $isRenew ? `${PRIME_PITCH_LAYOUT.renewPriceImageWidth}px` : 'none'};
    object-fit: contain;
    object-position: ${({ $isRenew }) => ($isRenew ? 'center' : 'left bottom')};
  }
`;

const TrialDivider = styled.div<{ $isRenew?: boolean }>`
  flex-shrink: 0;
  width: 1px;
  height: ${PRIME_PITCH_LAYOUT.trialDividerHeight}px;
  margin-bottom: ${({ $isRenew }) =>
    $isRenew ? 0 : PRIME_PITCH_LAYOUT.trialDividerTrialOffset};
  align-self: ${({ $isRenew }) => ($isRenew ? 'center' : 'auto')};
  background: ${PRIME_PITCH_COLORS.divider};
`;

const OfferCopy = styled.div<{ $isRenew?: boolean }>`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  width: ${({ $isRenew }) =>
    $isRenew
      ? PRIME_PITCH_LAYOUT.renewTitleCopyWidth
      : PRIME_PITCH_LAYOUT.trialCopyWidth}px;
  flex-shrink: 0;
  padding-bottom: ${({ $isRenew }) =>
    $isRenew ? 0 : PRIME_PITCH_LAYOUT.trialCopyTrialOffset};
`;

const TrialContinRow = styled.div`
  width: 100%;
  margin-top: ${PRIME_PITCH_LAYOUT.trialContinRowGap};
  text-align: center;
`;

const RenewTitle = styled.p`
  margin: 0;
  color: ${PRIME_PITCH_COLORS.white};
  font-size: ${PRIME_PITCH_TYPOGRAPHY.renewTitle.fontSize};
  line-height: ${PRIME_PITCH_TYPOGRAPHY.renewTitle.lineHeight};
  font-weight: ${Fonts.SEMIBOLD};
  text-align: center;
  white-space: pre-line;

  span,
  strong {
    font-weight: ${Fonts.SEMIBOLD};
  }
`;

const TrialPrimary = styled.p`
  margin: 0;
  color: ${PRIME_PITCH_COLORS.white};
  font-size: ${PRIME_PITCH_TYPOGRAPHY.trialPrimary.fontSize};
  line-height: ${PRIME_PITCH_TYPOGRAPHY.trialPrimary.lineHeight};
  font-weight: ${Fonts.SEMIBOLD};
  text-align: center;
  white-space: pre-line;
  letter-spacing: ${PRIME_PITCH_TYPOGRAPHY.trialPrimary.letterSpacing};

  span {
    font-weight: ${Fonts.REGULAR};
  }
`;

const TrialContin = styled.p`
  margin: 0;
  color: ${PRIME_PITCH_COLORS.mutedSoft};
  font-size: ${PRIME_PITCH_TYPOGRAPHY.trialContin.fontSize};
  line-height: ${PRIME_PITCH_TYPOGRAPHY.trialContin.lineHeight};
  text-align: center;
`;

const TaglineBlock = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${Spacings.SPACING_4B};
  width: 100%;
  margin: 0;
  overflow: visible;
`;

const TaglineCopy = styled.div`
  flex: 0 1 ${PRIME_PITCH_LAYOUT.taglineTextMaxWidth}px;
  max-width: ${PRIME_PITCH_LAYOUT.taglineTextMaxWidth}px;
  min-width: 0;
`;

const TagLine = styled.p`
  margin: 0;
  color: ${PRIME_PITCH_COLORS.white};
  font-size: ${PRIME_PITCH_TYPOGRAPHY.tagline.fontSize};
  line-height: ${PRIME_PITCH_TYPOGRAPHY.tagline.lineHeight};
  font-weight: ${Fonts.SEMIBOLD};
  white-space: pre-line;
`;

const PrimeTagImageWrap = styled.div`
  position: relative;
  flex-shrink: 0;
  width: ${PRIME_PITCH_LAYOUT.taglineImageWidth}px;
  height: ${PRIME_PITCH_LAYOUT.taglineImageHeight}px;
  margin-top: -${PRIME_PITCH_LAYOUT.taglineImagePullUp}px;
  margin-bottom: -${PRIME_PITCH_LAYOUT.taglineImagePullDown}px;
  overflow: visible;

  img {
    width: ${PRIME_PITCH_LAYOUT.taglineImageWidth}px !important;
    height: ${PRIME_PITCH_LAYOUT.taglineImageHeight}px !important;
    max-width: none;
    object-fit: contain;
  }
`;

const BenefitsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${Spacings.SPACING_4B};
  width: 100%;
`;

const BenefitsHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  gap: ${PRIME_PITCH_LAYOUT.benefitsHeaderGap};
  width: 100%;
`;

const BenefitsHeaderDecor = styled.img<{ $variant: 'left' | 'right' }>`
  flex: 0 0 ${PRIME_PITCH_LAYOUT.benefitsHeaderDecorWidth}px;
  width: ${PRIME_PITCH_LAYOUT.benefitsHeaderDecorWidth}px;
  height: ${PRIME_PITCH_LAYOUT.benefitsHeaderDecorHeight}px;
  display: block;
  transform: ${({ $variant }) =>
    $variant === 'left' ? 'scaleY(-1)' : 'rotate(180deg)'};
`;

const BenefitsHeaderText = styled.p`
  margin: 0;
  color: ${PRIME_PITCH_COLORS.white};
  font-size: ${PRIME_PITCH_TYPOGRAPHY.benefitsHeader.fontSize};
  line-height: ${PRIME_PITCH_TYPOGRAPHY.benefitsHeader.lineHeight};
  font-weight: ${Fonts.REGULAR};
  text-align: center;
  opacity: ${PRIME_PITCH_TYPOGRAPHY.benefitsHeader.opacity};
  white-space: nowrap;
`;

const BenefitsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${PRIME_PITCH_LAYOUT.benefitsListRowGap};
  padding: ${Spacings.SPACING_3B};
  border: 1px solid ${PRIME_PITCH_COLORS.cardBorder};
  border-radius: ${PRIME_PITCH_RADIUS.benefitsList};
  background: ${PRIME_PITCH_GRADIENTS.benefitsList};
  width: 100%;
  box-sizing: border-box;
`;

const BenefitRow = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacings.SPACING_2B};
  width: 100%;
`;

const BenefitCopy = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacings.SPACING_2B};
  min-width: 0;
`;

const BenefitCount = styled.span<{ $accent: string }>`
  color: ${({ $accent }) => $accent};
  font-size: ${PRIME_PITCH_TYPOGRAPHY.benefitCount.fontSize};
  line-height: ${PRIME_PITCH_TYPOGRAPHY.benefitCount.lineHeight};
  font-weight: ${Fonts.SEMIBOLD};
  flex-shrink: 0;
  width: ${PRIME_PITCH_LAYOUT.benefitCountWidth}px;
`;

const BenefitLabel = styled.span`
  color: ${PRIME_PITCH_COLORS.muted};
  font-size: ${PRIME_PITCH_TYPOGRAPHY.benefitLabel.fontSize};
  line-height: ${PRIME_PITCH_TYPOGRAPHY.benefitLabel.lineHeight};
  letter-spacing: ${PRIME_PITCH_TYPOGRAPHY.benefitLabel.letterSpacing};
  white-space: pre-line;
`;

const BenefitDivider = styled.img`
  width: 100%;
  height: 1px;
  display: block;
`;

const ctaShimmerMove = keyframes`
  from {
    transform: translateX(${PRIME_PITCH_LAYOUT.ctaShimmerStartOffset});
  }

  to {
    transform: translateX(${PRIME_PITCH_LAYOUT.ctaShimmerEndOffset});
  }
`;

const CTAContainer = styled.div`
  flex-shrink: 0;
  width: 100%;
  margin-top: auto;
  padding: ${Spacings.SPACING_4B} ${CONTENT_PADDING} ${Spacings.SPACING_7B};
  background: ${PRIME_PITCH_COLORS.surface};
  box-sizing: border-box;
`;

const PrimaryCta = styled.button`
  position: relative;
  overflow: hidden;
  width: 100%;
  border: ${PRIME_PITCH_LAYOUT.ctaBorderWidth} solid
    ${PRIME_PITCH_COLORS.accentBorder};
  border-radius: ${PRIME_PITCH_RADIUS.cta};
  height: ${PRIME_PITCH_LAYOUT.ctaHeight};
  background: ${PRIME_PITCH_COLORS.accent};
  color: ${PRIME_PITCH_COLORS.white};
  font-size: ${PRIME_PITCH_TYPOGRAPHY.cta.fontSize};
  font-weight: ${Fonts.SEMIBOLD};
  cursor: pointer;
  box-shadow: 0 0 7.8px ${PRIME_PITCH_COLORS.ctaGlow};
  isolation: isolate;

  &:hover {
    filter: brightness(1.06);
  }
`;

const CtaShimmerTrack = styled.span`
  position: absolute;
  inset: 0;
  z-index: 1;
  overflow: hidden;
  border-radius: ${PRIME_PITCH_RADIUS.ctaShimmer};
  pointer-events: none;
`;

const CtaShimmerGroup = styled.span`
  position: absolute;
  left: 0;
  top: ${PRIME_PITCH_LAYOUT.ctaShimmerTrackTop};
  width: 100%;
  height: ${PRIME_PITCH_LAYOUT.ctaShimmerTrackHeight}px;
  animation: ${ctaShimmerMove} ${PRIME_PITCH_LAYOUT.ctaShimmerDurationMs}ms
    ease-out infinite;
  will-change: transform;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }
`;

const CtaShimmerStreak = styled.span<{
  $left: string;
  $top: string;
  $width: number;
  $height: number;
  $rotate: number;
  $blur: number;
}>`
  position: absolute;
  left: ${({ $left }) => $left};
  top: ${({ $top }) => $top};
  width: ${({ $width }) => $width}px;
  height: ${({ $height }) => $height}px;
  background: ${PRIME_PITCH_COLORS.shimmer};
  mix-blend-mode: soft-light;
  transform: rotate(${({ $rotate }) => $rotate}deg);
  filter: blur(${({ $blur }) => $blur}px);
  pointer-events: none;
`;

const CtaLabel = styled.span`
  position: relative;
  z-index: 2;
`;

export default function PrimeTrialPromoModal({
  open,
  onClose,
  onJoinPrime,
  isRenew,
  heroVideoUrl,
  brandLogoSrc,
  primeLogoSrc,
  priceImageSrc,
  renewTitle,
  renewSubtitle,
  trialPrimaryText,
  trialContinText,
  tagLine,
  benefitsHeader,
  primaryCtaLabel,
  benefits
}: PrimeTrialPromoModalProps) {
  const isMobile = useIsMobile();
  const hasHeroVideo = Boolean(heroVideoUrl);
  const showTaglineBlock = Boolean(tagLine);
  const showBenefitsHeader = Boolean(benefitsHeader);
  const showOfferDivider = Boolean(
    priceImageSrc && (isRenew ? renewTitle : trialPrimaryText)
  );

  return (
    <StyledDialog
      open={open}
      onClose={onClose}
      $isMobile={isMobile}
      // @ts-expect-error MUI v4 Transition typing
      TransitionComponent={Transition}
    >
      <ModalSurface>
        <ScrollArea>
          {hasHeroVideo ? (
            <HeroFullBleed>
              <Hero>
                <Logo src={brandLogoSrc} alt="" width={32} height={32} />
                <VideoPreview src={heroVideoUrl} />
              </Hero>
              <HeroFade />
              <PrimeBadgeFloat>
                <PrimeBadgeImage
                  src={primeLogoSrc}
                  alt="Prime"
                  width={88}
                  height={31}
                  unoptimized
                />
              </PrimeBadgeFloat>
            </HeroFullBleed>
          ) : null}
          <PaddedContent>
            <OfferSection $hasHeroVideo={hasHeroVideo} $isRenew={isRenew}>
              <OfferBackdrop $isRenew={isRenew} aria-hidden>
                <OfferWaves src={PRIME_PITCH_ASSETS.offerCardWaves} alt="" />
              </OfferBackdrop>
              <TrialBlock $isRenew={isRenew}>
                {isRenew ? (
                  <>
                    <TrialTopRow $isRenew>
                      {renewTitle ? (
                        <OfferCopy $isRenew>
                          <RenewTitle>
                            {renderMultilineMarkedText(renewTitle)}
                          </RenewTitle>
                        </OfferCopy>
                      ) : null}
                      {showOfferDivider ? <TrialDivider $isRenew /> : null}
                      {priceImageSrc ? (
                        <PriceImageWrap $isRenew>
                          <Image
                            src={priceImageSrc}
                            alt=""
                            width={PRIME_PITCH_LAYOUT.renewPriceImageWidth}
                            height={PRIME_PITCH_LAYOUT.renewPriceImageHeight}
                            unoptimized
                          />
                        </PriceImageWrap>
                      ) : null}
                    </TrialTopRow>
                    {renewSubtitle ? (
                      <TrialContinRow>
                        <TrialContin>
                          {parseMarkedText(renewSubtitle, {
                            markedWeight: 'regular',
                            markedColor: PRIME_PITCH_COLORS.white
                          })}
                        </TrialContin>
                      </TrialContinRow>
                    ) : null}
                  </>
                ) : (
                  <>
                    <TrialTopRow>
                      {priceImageSrc ? (
                        <PriceImageWrap>
                          <Image
                            src={priceImageSrc}
                            alt=""
                            width={PRIME_PITCH_LAYOUT.priceImageWidth}
                            height={PRIME_PITCH_LAYOUT.priceImageHeight}
                            unoptimized
                          />
                        </PriceImageWrap>
                      ) : null}
                      {showOfferDivider ? <TrialDivider /> : null}
                      {trialPrimaryText ? (
                        <OfferCopy>
                          <TrialPrimary>
                            {renderMultilineMarkedText(trialPrimaryText, {
                              markedWeight: 'regular'
                            })}
                          </TrialPrimary>
                        </OfferCopy>
                      ) : null}
                    </TrialTopRow>
                    {trialContinText ? (
                      <TrialContinRow>
                        <TrialContin>
                          {parseMarkedText(trialContinText, {
                            markedWeight: 'regular',
                            markedColor: PRIME_PITCH_COLORS.white
                          })}
                        </TrialContin>
                      </TrialContinRow>
                    ) : null}
                  </>
                )}
              </TrialBlock>
            </OfferSection>

            {showTaglineBlock ? (
              <TaglineBlock>
                <TaglineCopy>
                  <TagLine>
                    {renderMultilineMarkedText(tagLine, {
                      highlightColor: PRIME_PITCH_COLORS.accent
                    })}
                  </TagLine>
                </TaglineCopy>
                <PrimeTagImageWrap>
                  <Image
                    src={PRIME_PITCH_ASSETS.primeBox}
                    alt=""
                    width={PRIME_PITCH_LAYOUT.taglineImageWidth}
                    height={PRIME_PITCH_LAYOUT.taglineImageHeight}
                    unoptimized
                  />
                </PrimeTagImageWrap>
              </TaglineBlock>
            ) : null}

            <BenefitsSection>
              {showBenefitsHeader ? (
                <BenefitsHeader>
                  <BenefitsHeaderDecor
                    $variant="left"
                    src={PRIME_PITCH_ASSETS.benefitsDividerLeft}
                    alt=""
                  />
                  <BenefitsHeaderText>{benefitsHeader}</BenefitsHeaderText>
                  <BenefitsHeaderDecor
                    $variant="right"
                    src={PRIME_PITCH_ASSETS.benefitsDividerRight}
                    alt=""
                  />
                </BenefitsHeader>
              ) : null}
              <BenefitsList>
                {benefits.map((benefit, index) => (
                  <div key={`${benefit.count}-${benefit.label}`}>
                    {index > 0 ? (
                      <BenefitDivider
                        src={PRIME_PITCH_ASSETS.benefitRowDivider}
                        alt=""
                      />
                    ) : null}
                    <BenefitRow>
                      <Image
                        src={benefit.logoSrc}
                        alt=""
                        width={PRIME_PITCH_LAYOUT.benefitIconSize}
                        height={PRIME_PITCH_LAYOUT.benefitIconSize}
                        unoptimized
                      />
                      <BenefitCopy>
                        <BenefitCount
                          $accent={
                            benefit.colour || DEFAULT_PRIME_PITCH_BENEFIT_COLOUR
                          }
                        >
                          {benefit.count}
                        </BenefitCount>
                        <BenefitLabel>{benefit.label}</BenefitLabel>
                      </BenefitCopy>
                    </BenefitRow>
                  </div>
                ))}
              </BenefitsList>
            </BenefitsSection>
          </PaddedContent>
        </ScrollArea>
        <CTAContainer>
          <PrimaryCta type="button" onClick={onJoinPrime}>
            <CtaShimmerTrack aria-hidden>
              <CtaShimmerGroup>
                {PRIME_PITCH_CTA_SHIMMER_STREAKS.map((streak) => (
                  <CtaShimmerStreak
                    key={`${streak.left}-${streak.rotate}`}
                    $left={streak.left}
                    $top={streak.top}
                    $width={streak.width}
                    $height={streak.height}
                    $rotate={streak.rotate}
                    $blur={streak.blur}
                  />
                ))}
              </CtaShimmerGroup>
            </CtaShimmerTrack>
            <CtaLabel>{primaryCtaLabel}</CtaLabel>
          </PrimaryCta>
        </CTAContainer>
      </ModalSurface>
    </StyledDialog>
  );
}
